import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { CircularProgress, Grid, TextField, Typography, useMediaQuery, useTheme, IconButton, Box, Paper, Collapse } from '@material-ui/core'
import AutocompleteNative from '@material-ui/lab/Autocomplete'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'

import { getClassificacaoLocalLabel, getLabelTipoVestigioFixado, getPopularesLabel } from 'helpers/labels'
import { CustomButton, CustomTable, ModalConfirmacao, SecaoGenerica } from 'components'
import { useStyles } from './FormLocal.styles'
import { useFormDadosBasicos, useFormCadaveres, useFormVestigiosFixados } from './hooks/useFormLocal'
import { FormVestigioFixado } from '../FormVestigioFixado'
import { FormAnaliseCadavericaLocal } from '../FormAnaliseCadavericaLocal'

const FormDadosBasicos = ({ idLocal, idExameRealizado, children, fetchLocais, cadaveresState, onConfirmSuccess }) => {
  const classes = useStyles()
  const {
    processandoLocal,
    values,
    handleChange,
    handleAutocompleteChange,
    confirmInformacoesGerais,
    processandoConfirmacao,
    cadaveresAnalisados,
    hasError,
    errors,
  } = useFormDadosBasicos({
    idLocal,
    idExameRealizado,
    fetchLocais,
    cadaveresState,
    onConfirmSuccess,
  })


  return (
    <div style={{ width: '100%' }}>
      {processandoLocal ? (
        <div className={classes.carregando}>
          <CircularProgress disableShrink />
        </div>
      ) : (
        <Grid container spacing={2}>
          <SecaoGenerica titulo="Informações gerais do local" spacing={2}>
            <Grid item md={12} sm={12} xs={12}>
              <TextField
                id="descricao"
                error={hasError('descricao')}
                fullWidth
                variant="outlined"
                label="Descrição"
                helperText={hasError('descricao') ? errors.descricao : null}
                name="descricao"
                multiline
                onChange={handleChange}
                value={values.descricao || ''}
                minRows={5}
                required
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item md={6} sm={6} xs={12}>
              <AutocompleteNative
                value={values.classificacao || null}
                name="classificacao"
                noOptionsText="Sem informações"
                options={['IMEDIATO', 'RELACIONADO']}
                onChange={(event, newValue) => {
                  handleAutocompleteChange('classificacao', newValue)
                }}
                getOptionLabel={(option) => `${getClassificacaoLocalLabel(option)}`}
                renderOption={(option) => `${getClassificacaoLocalLabel(option)}`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    error={hasError('classificacao')}
                    label="Classificação do local"
                    required
                    helperText={hasError('classificacao') ? errors.classificacao : null}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item sm={6} xs={12} md={6}>
              <AutocompleteNative
                value={values.tipoLocal || null}
                name="tipoLocal"
                noOptionsText="Sem informações"
                options={[
                  'AÇUDE',
                  'APARTAMENTO',
                  'ÁREA COMUM DE CONDOMÍNIO',
                  'PARQUE',
                  'ESCOLA',
                  'HOSPITAL',
                  'AVENIDA',
                  'CAMPO',
                  'ESTABELECIMENTO COMERCIAL',
                  'LAGOA',
                  'PRAIA',
                  'RUA',
                  'REGIÃO DE MATA',
                  'RESIDÊNCIA',
                  'RIO',
                  'OUTRO',
                ]}
                onChange={(event, newValue) => {
                  handleAutocompleteChange('tipoLocal', newValue)
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    error={hasError('tipoLocal')}
                    label="Tipo de local"
                    required
                    helperText={hasError('tipoLocal') ? errors.tipoLocal : null}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item sm={6} xs={12} md={6}>
              <TextField
                value={values.tipoLocalInputText || ''}
                variant="outlined"
                fullWidth
                label="Outro tipo de local (selecione a opção 'outro')"
                InputLabelProps={{
                  shrink: true,
                }}
                onChange={handleChange}
                name="tipoLocalInputText"
                disabled={values.tipoLocal !== 'OUTRO'}
              />
            </Grid>
            <Grid item md={6} sm={6} xs={12}>
              <AutocompleteNative
                value={values.populares || null}
                name="populares"
                noOptionsText="Sem informações"
                options={['PRESENTES - FORA DA ZONA', 'PRESENTES - DENTRO DA ZONA', 'SEM POPULARES']}
                getOptionSelected={(option, value) => option === value}
                onChange={(event, newValue) => {
                  handleAutocompleteChange('populares', newValue)
                }}
                getOptionLabel={(option) => `${getPopularesLabel(option)}`}
                renderOption={(option) => `${getPopularesLabel(option)}`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    error={hasError('populares')}
                    label="Presença de populares"
                    helperText={hasError('populares') ? errors.populares : null}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item md={6} sm={6} xs={12}>
              <AutocompleteNative
                value={values.isolamento || null}
                name="isolamento"
                noOptionsText="Sem informações"
                options={['DEVIDAMENTE ISOLADO', 'PARCIALMENTE ISOLADO', 'NÃO ISOLADO']}
                onChange={(event, newValue) => {
                  handleAutocompleteChange('isolamento', newValue)
                }}
                getOptionLabel={(option) => `${option}`}
                renderOption={(option) => `${option}`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Quanto ao isolamento"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item md={6} sm={6} xs={12}>
              <AutocompleteNative
                value={values.idoneidade || null}
                name="idoneidade"
                noOptionsText="Sem informações"
                options={['IDÔNEO', 'INIDÔNEO', 'NÃO DEFINIDO']}
                onChange={(event, newValue) => {
                  handleAutocompleteChange('idoneidade', newValue)
                }}
                getOptionLabel={(option) => `${option}`}
                renderOption={(option) => `${option}`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Quanto à idoneidade"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                )}
              />
            </Grid>
          </SecaoGenerica>

          {children}


          <Grid item md={12} sm={12} xs={12} className={classes.botaoSalvar}>
            <CustomButton
              variant="contained"
              color="primary"
              disabled={
                !values.descricao ||
                !values.classificacao ||
                !values.tipoLocal ||
                !values.isolamento ||
                !values.idoneidade ||
                !cadaveresAnalisados() ||
                processandoConfirmacao
              }
              isLoading={processandoConfirmacao}
              onClick={confirmInformacoesGerais}
            >
              CONFIRMAR INFORMAÇÕES DO LOCAL
            </CustomButton>
          </Grid>
        </Grid>
      )}
    </div>
  )
}

FormDadosBasicos.propTypes = {
  idLocal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  idExameRealizado: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  children: PropTypes.node,
  fetchLocais: PropTypes.func.isRequired,
  cadaveresState: PropTypes.array.isRequired,
}

const FormCadaveres = ({ idExameRealizado, idLocal, setCadaveresState }) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  const { cadaveres, processandoCadaveres, fetchCadaveres } = useFormCadaveres({
    idLocal,
    idExameRealizado,
    setCadaveresState,
  })

  const [cadaverExpandidoId, setCadaverExpandidoId] = useState(null)

  const handleToggleExpandir = (id) => {
    setCadaverExpandidoId((prev) => (prev === id ? null : id))
  }

  const columns = [
    {
      title: 'NIC',
      field: 'nic',
    },
    {
      title: 'Descrição',
      field: 'descricaoGeral',
    },
    {
      title: 'Análise',
      render: (rowData) =>
        rowData.anaCadLocs && rowData.anaCadLocs.length ? (
          <Typography className={classes.positive} style={{ fontSize: '12px' }}>
            Cadáver analisado
          </Typography>
        ) : (
          <Typography className={classes.negative} style={{ fontSize: '12px' }}>
            Cadáver não analisado
          </Typography>
        ),
    },
  ]

  return (
    <SecaoGenerica titulo="Cadáveres associados ao local">
      <Grid item md={12} sm={12} xs={12} className={classes.tableContainer}>
        {processandoCadaveres ? (
          <div className={classes.carregando}>
            <CircularProgress disableShrink />
          </div>
        ) : isMobile ? (
          cadaveres && cadaveres.length > 0 ? (
            <Grid container spacing={2}>
              {cadaveres.map((item) => {
                const isExpanded = cadaverExpandidoId === item.id
                const analisado = item.anaCadLocs && item.anaCadLocs.length > 0
                return (
                  <Grid item xs={12} key={item.id}>
                    <Paper className={classes.cadaverCard}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        onClick={() => handleToggleExpandir(item.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle2"
                            style={{ fontWeight: 'bold', fontSize: '14px' }}
                          >
                            NIC: {item.nic || '-'}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ fontSize: '12px', marginTop: '4px' }}
                          >
                            {item.descricaoGeral || 'Sem descrição cadastrada'}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          {analisado ? (
                            <span className={classes.chipStatusSuccess}>Analisado</span>
                          ) : (
                            <span className={classes.chipStatusWarning}>Pendente</span>
                          )}
                          <IconButton size="small" style={{ marginLeft: '8px' }}>
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Box>
                      </Box>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box className={classes.expandedFormContainer}>
                          <FormAnaliseCadavericaLocal
                            idCadaver={item.id}
                            fetchCadaveres={fetchCadaveres}
                            idExameRealizado={idExameRealizado}
                            onConfirmSuccess={() => setCadaverExpandidoId(null)}
                          />
                        </Box>
                      </Collapse>
                    </Paper>
                  </Grid>
                )
              })}
            </Grid>
          ) : (
            <Typography
              style={{
                fontSize: '12px',
                padding: '24px',
                textAlign: 'center',
                color: '#999',
                fontStyle: 'italic',
              }}
            >
              Nenhum cadáver associado a este local
            </Typography>
          )
        ) : (
          <CustomTable
            title=""
            columns={columns}
            data={cadaveres}
            onRowClick={(event, rowData, togglePanel) => togglePanel()}
            detailPanel={(rowData) => (
              <FormAnaliseCadavericaLocal
                idCadaver={rowData.id}
                fetchCadaveres={fetchCadaveres}
                idExameRealizado={idExameRealizado}
              />
            )}
            options={{
              search: false,
              pageSize: 5,
              paging: false,
              headerStyle: {
                backgroundColor: '#f4f6f8',
                fontSize: '12px',
              },
              toolbar: false,
            }}
          />
        )}
      </Grid>
    </SecaoGenerica>
  )
}

FormCadaveres.propTypes = {
  idLocal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  idExameRealizado: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  setCadaveresState: PropTypes.func.isRequired,
}

const FormVestigiosFixados = ({ idExameRealizado, idLocal }) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

  const {
    vestigiosFixados,
    processandoVestigiosFixados,
    setVestigiosFixados,
    setProcessandoVestigiosFixados,
    openFormVestigioFixado,
    setOpenFormVestigioFixado,
    openAlertDeletarVestigioFixado,
    handleClickDeletarVestigioFixado,
    handleCloseDeletarVestigioFixado,
    handleBotaoAdicionarVestigioFixado,
    confirmDeletarVestigioFixado,
  } = useFormVestigiosFixados({
    idLocal,
    idExameRealizado,
  })

  const rightAction = (
    <CustomButton colorType="primary" variant="contained" onClick={handleBotaoAdicionarVestigioFixado}>
      <AddIcon className={classes.addIcon} />
      Adicionar vestígio fixado
    </CustomButton>
  )

  const columns = [
    {
      title: 'Tipo do vestígio',
      render: (rowData) => getLabelTipoVestigioFixado(rowData),
    },
    {
      title: 'Marca',
      render: (rowData) => (rowData.marca ? rowData.marca : '-'),
    },
    {
      title: 'Calibre',
      render: (rowData) => (rowData.calibre ? rowData.calibre : '-'),
    },
    {
      title: 'Quantidade',
      field: 'quantidade',
    },
    {
      title: 'Localização',
      field: 'localizacao',
    },
    {
      title: 'Observações',
      field: 'observacao',
    },
  ]

  return (
    <SecaoGenerica titulo="Vestígios fixados no local" rightAction={rightAction}>
      <Grid item md={12} sm={12} xs={12} className={classes.tableContainer}>
        {processandoVestigiosFixados ? (
          <div className={classes.carregando}>
            <CircularProgress disableShrink />
          </div>
        ) : isMobile ? (
          vestigiosFixados && vestigiosFixados.length > 0 ? (
            <Grid container spacing={2}>
              {vestigiosFixados.map((item) => (
                <Grid item xs={12} key={item.id}>
                  <Paper className={classes.vestigioCard}>
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={1.5}>
                      <Typography
                        variant="subtitle2"
                        color="primary"
                        style={{ fontWeight: 'bold', fontSize: '14px' }}
                      >
                        {getLabelTipoVestigioFixado(item)}
                      </Typography>
                      <IconButton size="small" onClick={() => handleClickDeletarVestigioFixado(item)} style={{ padding: 4 }}>
                        <DeleteIcon color="error" fontSize="small" />
                      </IconButton>
                    </Box>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="body2" style={{ fontSize: '12px' }}>
                          <b>Marca:</b> {item.marca || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" style={{ fontSize: '12px' }}>
                          <b>Calibre:</b> {item.calibre || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" style={{ fontSize: '12px' }}>
                          <b>Quantidade:</b> {item.quantidade || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" style={{ fontSize: '12px' }}>
                          <b>Localização:</b> {item.localizacao || '-'}
                        </Typography>
                      </Grid>
                      {item.observacao && (
                        <Grid item xs={12}>
                          <Box mt={0.5} pt={0.5} borderTop="1px dashed #eee">
                            <Typography variant="body2" style={{ fontSize: '12px' }}>
                              <b>Observações:</b> {item.observacao}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography
              style={{
                fontSize: '12px',
                padding: '24px',
                textAlign: 'center',
                color: '#999',
                fontStyle: 'italic',
              }}
            >
              Nenhum vestígio fixado cadastrado neste local
            </Typography>
          )
        ) : (
          <CustomTable
            title=""
            columns={columns}
            data={vestigiosFixados}
            options={{
              search: false,
              pageSize: 5,
              paging: false,
              headerStyle: {
                backgroundColor: '#f4f6f8',
                fontSize: '12px',
              },
              toolbar: false,
            }}
            actions={[
              {
                icon: () => <DeleteIcon />,
                tooltip: 'Deletar vestígio fixado',
                onClick: (event, rowData) => handleClickDeletarVestigioFixado(rowData),
              },
            ]}
          />
        )}
      </Grid>
      <FormVestigioFixado
        idExameRealizado={idExameRealizado}
        idLocal={idLocal}
        open={openFormVestigioFixado}
        setOpen={setOpenFormVestigioFixado}
        setVestigiosFixados={setVestigiosFixados}
        setProcessandoVestigiosFixados={setProcessandoVestigiosFixados}
      />

      <ModalConfirmacao
        aberto={openAlertDeletarVestigioFixado}
        onClose={handleCloseDeletarVestigioFixado}
        onConfirm={confirmDeletarVestigioFixado}
        titulo="Confirmar exclusão"
        confirmarColor="error"
        confirmarLabel="Confirmar Exclusão"
        processando={processandoVestigiosFixados}
      >
        Tem certeza que deseja deletar este vestígio fixado? Esta ação não pode ser desfeita.
      </ModalConfirmacao>
    </SecaoGenerica>
  )
}


FormVestigiosFixados.propTypes = {
  idLocal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  idExameRealizado: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
}

export const FormLocal = ({ idLocal, fetchLocais, idExameRealizado, onConfirmSuccess }) => {
  const classes = useStyles()
  const [cadaveresState, setCadaveresState] = useState([])

  return (
    <div className={classes.detailPanel}>
      <Grid container spacing={0} className={classes.containerCampos}>
        <FormDadosBasicos
          idLocal={idLocal}
          idExameRealizado={idExameRealizado}
          fetchLocais={fetchLocais}
          cadaveresState={cadaveresState}
          onConfirmSuccess={onConfirmSuccess}
        >
          <FormCadaveres
            idLocal={idLocal}
            idExameRealizado={idExameRealizado}
            setCadaveresState={setCadaveresState}
          />
          <FormVestigiosFixados idExameRealizado={idExameRealizado} idLocal={idLocal} />
        </FormDadosBasicos>
      </Grid>
    </div>
  )
}

FormLocal.propTypes = {
  idLocal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  fetchLocais: PropTypes.func.isRequired,
  idExameRealizado: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
}
